// threadHealth — derives a 3-state health indicator for a thread based on
// time since last inbound + last message direction + (optional) Shopify order
// proximity to production.
//
// States:
//   ontrack — outbound was last, OR last inbound was <1h ago
//   aging   — last inbound is 1–6h old AND no outbound since
//   urgent  — last inbound is >6h old AND no outbound since (or order in
//             production with unanswered inbound)

export type ThreadHealth = 'ontrack' | 'aging' | 'urgent';

export interface ThreadHealthInput {
  lastMessageAt: string;
  lastMessageDirection: 'inbound' | 'outbound' | null;
  /** Optional — true if there's a Shopify order in production for this thread. */
  hasOrderInProduction?: boolean;
}

export interface ThreadHealthResult {
  state: ThreadHealth;
  label: string;
  /** Hours (rounded) since the last inbound. Useful for tooltips. */
  hoursSinceLast: number;
}

const HOUR = 3_600_000;

export function computeThreadHealth(input: ThreadHealthInput): ThreadHealthResult {
  const ageMs = Math.max(0, Date.now() - new Date(input.lastMessageAt).getTime());
  const hoursSinceLast = Math.round(ageMs / HOUR);

  if (input.lastMessageDirection === 'outbound') {
    return {
      state: 'ontrack',
      label: 'On track — waiting on customer',
      hoursSinceLast,
    };
  }

  if (ageMs < HOUR) {
    return {
      state: 'ontrack',
      label: `On track — fresh (${hoursSinceLast || 'under 1'}h)`,
      hoursSinceLast,
    };
  }

  if (ageMs < 6 * HOUR) {
    const prodSuffix = input.hasOrderInProduction ? ' — order moves to production today' : '';
    return {
      state: 'aging',
      label: `Aging — reply within ${Math.max(1, 6 - hoursSinceLast)}h${prodSuffix}`,
      hoursSinceLast,
    };
  }

  return {
    state: 'urgent',
    label: `Urgent — ${hoursSinceLast}h since their last message`,
    hoursSinceLast,
  };
}
